// how-to-start/utils/team-roster.ts
//
// REAL PEOPLE ON A FUNNEL-GENERATED TEAM SLIDE.
//
// The editor's add-slide path researches a company's actual team before it
// writes a people slide, and refuses to name anyone it could not verify. The
// funnel had none of that: it sends the outline to docpilot's /generate-slide,
// which has no web search and no idea who works at the customer's company, and
// whose prompt cheerfully said "expand using your knowledge".
//
// Asked for Storydoc's founders it answered "Idan Shpizear, Co-Founder & CEO"
// and "Mads Fuhr, Co-Founder & CTO" -- real people, at other companies,
// presented as this company's founders on a deck a customer would send to
// investors. Not a formatting problem: a false statement about named
// individuals.
//
// So the funnel now does what the editor does: it researches the deck's
// company and sends the verified roster with the slides.
//
// FETCHED ONCE FOR THE WHOLE DECK, NOT PER DETECTED SLIDE. The first version
// looked for people slides by keyword and researched only those, which failed
// the moment it met a real outline -- the titles are written by a model, and
// "Meet the Visionaries Behind Storydoc", "The Faces Behind the Press" and a
// bare "Leadership" all slipped past a matcher tuned for "team" and
// "founders". A slide with no roster then names people from memory, which is
// the whole problem. Guessing which slides will name someone is not a job
// worth doing carefully when the answer can simply be available to all of
// them.
//
// So the deck's roster is researched once, in the background, and any slide
// that could name a person waits for it. That test is deliberately generous:
// a false positive costs a wait on a request that is already running, while a
// false negative costs a customer a false claim about real people.
//
// Where research cannot confirm anyone, nothing is sent and the prompt writes
// "Add name here" -- a blank the customer fills in, rather than a name they
// might never check.

import {
  isTeamRequest,
  teamCompanyFromRequest,
  wantsFoundersOnly,
} from "@editor/modules/header/ai/helpers/team-intent";
import {
  formatRosterForPrompt,
  foundersOnly,
  researchTeam,
} from "@editor/modules/header/ai/pipelines/research-team";
import type { RunPrompt } from "@editor/modules/header/ai/pipelines/research-team";
import type { OutlineItem } from "../types/generation";
import { delay } from "./run-with-concurrency";

/** Everything about an outline item that could name people. */
const textOf = (slide: OutlineItem): string =>
  [slide.title, ...(slide.content ?? []), ...(slide.dataPoints ?? [])]
    .filter(Boolean)
    .join(" ");

/**
 * Whether this slide could end up naming a person.
 *
 * Not "is this a team slide" -- that question is answered on a title the model
 * invented, and it got "Meet the Visionaries Behind Storydoc" wrong. This is
 * the loosest net that still spares an obvious pricing or roadmap slide from
 * waiting: anything about people, the company's story, or who is behind it.
 *
 * Erring towards yes is deliberate. The roster request is already in flight
 * and shared, so a false positive waits on something that was going to finish
 * anyway; a false negative is an invented name on a customer's deck.
 */
const COULD_NAME_PEOPLE =
  /\b(?:team|teams|people|person|staff|colleague|founder|founders|founded|co-?founder|leadership|leader|leaders|executive|executives|management|board|advisor|advisers|advisors|visionar\w+|face|faces|mind|minds|crew|member|members|who\s+we\s+are|behind|meet|our\s+story|about\s+us|bio|bios|biography|profile|profiles|expert|experts|talent|CEO|CTO|CPO|COO|CFO)\b/i;

export const mightNamePeople = (slide: OutlineItem): boolean =>
  COULD_NAME_PEOPLE.test(textOf(slide));

/** Whether this outline item is unambiguously asking for a company's people. */
export const isPeopleSlide = (slide: OutlineItem): boolean =>
  isTeamRequest(textOf(slide));

/**
 * The company a people slide is about, and which of its people.
 *
 * The slide's own words win over the deck's brand: a custom outline slide
 * reading "Meet the Storydoc founders" on a deck for someone else is about
 * Storydoc, and researching the deck's brand there would put real people from
 * the wrong company on the slide -- the exact failure this exists to stop.
 */
export const teamSubjectOf = (
  slide: OutlineItem,
  deckCompany: string | null,
): { company: string | null; focus: "" | "founders" } => {
  const text = textOf(slide);
  return {
    company: teamCompanyFromRequest(text) ?? deckCompany,
    focus: wantsFoundersOnly(text) ? "founders" : "",
  };
};

/**
 * The longest a slide waits for the roster. Research is a web search that can
 * run twice; past this the slide is built without it and writes placeholders.
 */
export const ROSTER_WAIT_MS = 20_000;

/** How many people a slide has room for, before the layout is known. */
const WANTED = 6;

/** One research call, formatted for the prompt, or null if nothing was confirmed. */
const research = async (
  runPrompt: RunPrompt,
  brain: unknown,
  company: string,
  focus: "" | "founders",
): Promise<string | null> => {
  try {
    const people = await researchTeam(
      runPrompt,
      brain as never,
      WANTED,
      company,
      focus,
    );
    if (!people?.length) return null;
    const chosen = focus === "founders" ? foundersOnly(people) : people;
    return formatRosterForPrompt(chosen, focus === "founders");
  } catch {
    // Research failing is not the deck failing. No roster means the prompt
    // writes placeholders, which is the safe answer.
    return null;
  }
};

/**
 * A roster lookup for one deck.
 *
 * The deck's own company is researched IMMEDIATELY and in the background, so
 * the answer is usually waiting by the time a slide needs it. A slide that
 * names a DIFFERENT company gets its own lookup -- a custom slide about
 * Storydoc on an agency's deck is about Storydoc, and answering it with the
 * agency's people would be the original bug wearing a different hat.
 *
 * Every distinct company-and-focus is fetched once and shared.
 */
export const createRosterLookup = (
  runPrompt: RunPrompt,
  brain: unknown,
  deckCompany: string | null,
  waitMs = ROSTER_WAIT_MS,
) => {
  const inFlight = new Map<string, Promise<string | null>>();

  const get = (company: string, focus: "" | "founders") => {
    const key = `${company.toLowerCase()}::${focus}`;
    const existing = inFlight.get(key);
    if (existing) return existing;
    const started = research(runPrompt, brain, company, focus);
    inFlight.set(key, started);
    return started;
  };

  // Started now, not when the first slide asks: by then the deck is generating
  // twenty slides at once and every one of them would be waiting on it.
  if (deckCompany) void get(deckCompany, "");

  return async (slide: OutlineItem): Promise<string | null> => {
    if (!mightNamePeople(slide)) return null;
    const { company, focus } = teamSubjectOf(slide, deckCompany);
    if (!company) return null;
    return Promise.race([get(company, focus), delay(waitMs).then(() => null)]);
  };
};

export type RosterLookup = ReturnType<typeof createRosterLookup>;
